import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { usePageAccessGuard } from "@/hooks/usePageAccessGuard";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { UnifiedItemsContainer } from "@/components/layout/page-containers";
import { PageHandlers, TransformContext } from "@/components/layout/page-containers/UnifiedItemsContainer/types";
import { variablesConfig } from "./unified-items/configs/variablesConfig";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";

// Import stores and types
import {
  TypeItem,
  TypeElement,
  typesStore,
  createType,
  updateType,
  deleteType,
  fetchTypes,
} from "@/lib/stores/typesStore";
import { variablesDataSource } from "@/lib/dataSources/sources/variables";
import VariablesWidget from "@/components/cards/widgets/VariablesWidget";

/**
 * VariablesPage - SOURCE OF TRUTH for variable operations
 * 
 * This page owns all variable-specific:
 * - Store operations (write/update/delete in typesStore)
 * - CRUD handlers (create, update, delete variables)
 * - Card transformation (raw TypeItem → CardData)
 * - Wizard callbacks (what happens after create/update)
 * 
 * When the structure of variables changes, update THIS file.
 */
export default function VariablesPage() {
  usePageAccessGuard('variables');
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { selectedGame } = useSimplifiedUserData();

  // #region Store Operations
  /**
   * Variable-specific store operations
   */
  const storeOperations = useMemo(() => ({
    writeToStore: (items: TypeItem[]) => {
      typesStore.setData(items);
    },
    updateInStore: (id: string, data: Partial<TypeItem>) => {
      const current = typesStore.getData();
      const updated = current.map(item => 
        item.id === id ? { ...item, ...data } : item
      );
      typesStore.setData(updated);
    },
    deleteFromStore: (id: string) => {
      const current = typesStore.getData();
      typesStore.setData(current.filter(item => item.id !== id));
    },
  }), []);
  // #endregion

  // #region CRUD Handlers
  /**
   * Variable-specific CRUD handlers
   */
  const handlers = useMemo(() => ({
    onCreate: async (data: any, folderId?: string): Promise<TypeItem> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      const variableData = {
        ...data,
        game_id: data.game_id || selectedGame.id,
        folder: folderId || data.folder || null,
      };

      const createdVariable = await createType(variableData, toast);
      
      // Update local store
      const current = typesStore.getData();
      typesStore.setData([...current, createdVariable]);
      
      return createdVariable;
    },

    onUpdate: async (id: string, data: Partial<TypeItem>): Promise<TypeItem> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      const existingVariable = typesStore.getData().find(item => item.id === id);
      if (!existingVariable) {
        throw new Error("Variable not found");
      }

      const variableData: {
        id: string;
        game_id: string;
        name: string;
        element_type: string;
        group?: string;
        description?: string;
        data: TypeElement[];
        folder?: string;
      } = {
        id,
        game_id: data.game_id || selectedGame.id,
        name: data.name ?? existingVariable.name,
        element_type: data.element_type ?? existingVariable.element_type,
        group: data.group_name ?? existingVariable.group_name,
        description: data.description ?? existingVariable.description,
        data: data.data ?? existingVariable.data ?? [],
        folder: data.folder ?? existingVariable.folder,
      };

      const updatedVariable = await updateType(variableData, toast);
      storeOperations.updateInStore(id, updatedVariable);
      
      return updatedVariable;
    },

    onDelete: async (id: string): Promise<void> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      await deleteType(id, selectedGame.id, toast);
      storeOperations.deleteFromStore(id);
    },

    onBulkDelete: async (ids: string[]): Promise<void> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      for (const id of ids) {
        await deleteType(id, selectedGame.id, toast);
        storeOperations.deleteFromStore(id);
      }
    },
  }), [selectedGame, toast, storeOperations]);
  // #endregion

  // #region Card Transformation
  /**
   * Transform variable items to CardData for display
   */
  const transformToCard = useCallback((item: TypeItem, context: TransformContext): CardData => {
    const cards = variablesDataSource.transformers.toCard?.([item], {
      tags: context.tags,
      blueprints: context.blueprints || [],
      getUserNameById: context.getUserNameById,
    }) || [];
    
    return cards[0] || {
      id: item.id,
      title: item.name,
      gameId: item.game_id,
      folder: item.folder,
      tags: [],
    };
  }, []);

  /**
   * Render a variable card using VariablesWidget
   */
  const renderCard = useCallback((card: CardData, tags: any[]) => {
    return (
      <VariablesWidget
        id={card.id}
        gameId={card.gameId}
        title={card.title}
        elementType={card.elementType as string || 'string'}
        elementTypeLabel={card.elementTypeLabel as string || 'String'}
        elementCount={card.elementCount as number || 0}
        description={card.description}
        groupName={card.groupName as string}
        lastUpdate={card.lastUpdate}
        folder={card.folder as string}
        itemType="variables"
        shadowType={card.shadowType as 'color' | 'black' | 'gradient' | 'none' | 'status'}
        shadowColor={card.shadowColor}
        onDelete={card.onDelete}
        onEdit={card.onEdit}
      />
    );
  }, []);
  // #endregion

  // #region Edit Handler
  /**
   * Handle edit action for variables
   */
  const onEdit = useCallback((id: string, data: TypeItem[], navigateFn: (path: string) => void) => {
    const variable = data.find(item => item.id === id);
    navigate(`/variables/${id}`, { state: { itemName: variable?.name } });
  }, [navigate]);
  // #endregion

  // #region Wizard Callbacks
  /**
   * Variable-specific wizard callbacks
   */
  const wizardCallbacks = useMemo(() => ({
    onItemCreated: async (item: TypeItem) => {
      toast({
        title: "Success",
        description: `Variable "${item.name}" created successfully`,
      });
      
      if (selectedGame?.id) {
        try {
          await fetchTypes(selectedGame.id);
        } catch (error) {
          console.warn("Failed to refresh variables after creation:", error);
        }
      }
    },

    onItemUpdated: async (item: TypeItem) => {
      toast({
        title: "Success",
        description: `Variable "${item.name}" updated successfully`,
      });
      
      if (selectedGame?.id) {
        try {
          await fetchTypes(selectedGame.id);
        } catch (error) {
          console.warn("Failed to refresh variables after update:", error);
        }
      }
    },

    onWizardClose: () => {},
  }), [toast, selectedGame]);
  // #endregion

  // #region Combined PageHandlers
  const pageHandlers: PageHandlers<TypeItem> = useMemo(() => ({
    storeOperations,
    handlers,
    transformToCard,
    renderCard,
    wizardCallbacks,
    onEdit,
    features: {
      supportsPendingChanges: false,
      supportsBulkCreate: false,
      supportsFlowView: false,
    },
  }), [storeOperations, handlers, transformToCard, renderCard, wizardCallbacks, onEdit]);
  // #endregion

  return (
    <UnifiedItemsContainer 
      itemType="variables"
      handlers={pageHandlers}
      config={variablesConfig}
    />
  );
}
